import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import Button from "components/Button";
import { Board, ContentArea, FooterArea, FormRow, TitleArea } from "components/LayoutParts";
import RadioButton from "components/RadioButton";
import TextForm from "components/TextForm";
import UserModal, { UserModalConfig } from "components/UserModal";
import color from "constants/color";
import font from "constants/font";
import { PIT_PORT_OWNER_ENTITY_TYPE, PIT_PORT_OWNER_ENTITY_TYPE_LABEL } from "pages/PitPort/OwnerEntity/constants";
import {
  validateEmail,
  validateRequired,
  validateTel,
  validateProfitShareRate,
} from "pages/PitPort/OwnerEntity/schema";

const DeleteWrap = styled.div`
  display: flex;
  flex: auto;
  justify-content: flex-end;
`;

const Label = styled.div`
  min-width: 120px;
  margin: 0 5px;
`;

const CancelWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;
const SaveWrap = styled(CancelWrap)`
  align-items: center;
  justify-content: flex-end;
`;

const Annotation = styled.span`
  font-size: ${font.size12};
  color: ${color.text};
  margin-right: 16px;
`;

interface BaseOwnerEntityFormProps {
  mode: "create" | "edit";
  initialData?: {
    name?: string;
    address?: string;
    tel?: string;
    email?: string;
    type?: "individual_ps" | "corporation_ps" | "corporation_oem";
    profitShareRate?: number;
  };
  onSubmit: (data: {
    name: string;
    address: string;
    tel: string;
    email: string;
    type: "individual_ps" | "corporation_ps" | "corporation_oem";
    profitShareRate: number;
  }) => Promise<{ status: number; message?: string }>;
  onCancel: () => void;
  breadcrumbItems: { pageName: string; onClick: () => void }[];
  currentPageName: string;
}

const BaseOwnerEntityForm = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  breadcrumbItems,
  currentPageName,
}: BaseOwnerEntityFormProps) => {
  const navigate = useNavigate();
  const [name, setName] = useState(initialData?.name || "");
  const [address, setAddress] = useState(initialData?.address || "");
  const [tel, setTel] = useState(initialData?.tel || "");
  const [email, setEmail] = useState(initialData?.email || "");
  const [type, setType] = useState(initialData?.type || "individual_ps");
  const [profitShareRate, setProfitShareRate] = useState(
    initialData?.profitShareRate ? initialData.profitShareRate * 100 : 0
  );

  // Validation states
  const [nameError, setNameError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [telError, setTelError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [profitShareRateError, setProfitShareRateError] = useState(false);

  const changeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(!validateRequired(value));
  };

  const changeAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);
    setAddressError(!validateRequired(value));
  };

  const changeTel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTel(value);
    setTelError(!validateTel(value));
  };

  const changeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(!validateEmail(value));
  };

  const changeType = (e: React.ChangeEvent<HTMLInputElement>) =>
    setType(e.target.value as "individual_ps" | "corporation_ps" | "corporation_oem");

  const changeProfitShareRate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setProfitShareRate(value);
    setProfitShareRateError(!validateProfitShareRate(value));
  };

  const canSubmit =
    name &&
    address &&
    tel &&
    email &&
    type &&
    profitShareRate &&
    !nameError &&
    !addressError &&
    !telError &&
    !emailError &&
    !profitShareRateError;

  const [isModalShow, setIsModalShow] = useState(false);
  const [modalConfig, setModalConfig] = useState<UserModalConfig>({
    onClickOutside: () => {
      return;
    },
    subtitle: "完了しました",
    main: {
      buttonType: "secondary",
      onClick: () => {
        return;
      },
      label: "閉じる",
    },
  });

  const showModal = (config: UserModalConfig) => {
    setModalConfig(config);
    setIsModalShow(true);
  };

  const hideModal = () => {
    setIsModalShow(false);
  };

  const onClickSubmit = useCallback(async () => {
    const { status: responseStatus, message } = await onSubmit({
      name,
      address,
      tel,
      email,
      type,
      profitShareRate: profitShareRate / 100,
    });

    responseStatus === 200 || responseStatus === 201
      ? showModal({
          onClickOutside: hideModal,
          subtitle: mode === "create" ? "貸主を追加しました" : "変更内容を保存しました",
          main: {
            buttonType: "secondary",
            onClick: () => navigate("/pit_port/owner-entities"),
            label: "閉じる",
          },
        })
      : showModal({
          onClickOutside: hideModal,
          subtitle: "送信に失敗しました",
          main: {
            buttonType: "secondary",
            onClick: hideModal,
            label: "閉じる",
          },
          errorMessage: responseStatus === 400 ? message : "時間をおいてもう一度お試しください",
        });
  }, [name, address, tel, email, type, profitShareRate, onSubmit, navigate, mode]);

  return (
    <>
      <Board>
        <TitleArea>
          <Breadcrumb currentPageName={currentPageName} breadcrumbItems={breadcrumbItems} />
          <DeleteWrap />
        </TitleArea>
        <ContentArea>
          <FormRow>
            <Label>貸主名</Label>
            <TextForm
              type="text"
              width="600px"
              value={name}
              placeholder="例）ランディット株式会社"
              required
              onChange={changeName}
              isError={nameError}
              errorMessage="貸主名を入力してください"
            />
          </FormRow>
          <FormRow>
            <Label>住所</Label>
            <TextForm
              type="text"
              width="600px"
              value={address}
              placeholder="例）東京都千代田区永田町1-7-1"
              required
              onChange={changeAddress}
              isError={addressError}
              errorMessage="住所を入力してください"
            />
          </FormRow>
          <FormRow>
            <Label>電話番号</Label>
            <TextForm
              type="text"
              width="600px"
              value={tel}
              placeholder="例）03-1234-5678"
              required
              onChange={changeTel}
              isError={telError}
              errorMessage="有効な電話番号を入力してください"
            />
          </FormRow>
          <FormRow>
            <Label>メールアドレス</Label>
            <TextForm
              type="text"
              width="600px"
              value={email}
              placeholder="例）info@example.com"
              required
              onChange={changeEmail}
              isError={emailError}
              errorMessage="有効なメールアドレスを入力してください"
            />
          </FormRow>
          <FormRow>
            <Label>種類</Label>
            <RadioButton
              id="type"
              choices={[
                {
                  label: PIT_PORT_OWNER_ENTITY_TYPE_LABEL[PIT_PORT_OWNER_ENTITY_TYPE.INDIVIDUAL_PS],
                  value: PIT_PORT_OWNER_ENTITY_TYPE.INDIVIDUAL_PS,
                },
                {
                  label: PIT_PORT_OWNER_ENTITY_TYPE_LABEL[PIT_PORT_OWNER_ENTITY_TYPE.CORPORATION_PS],
                  value: PIT_PORT_OWNER_ENTITY_TYPE.CORPORATION_PS,
                },
                {
                  label: PIT_PORT_OWNER_ENTITY_TYPE_LABEL[PIT_PORT_OWNER_ENTITY_TYPE.CORPORATION_OEM],
                  value: PIT_PORT_OWNER_ENTITY_TYPE.CORPORATION_OEM,
                },
              ]}
              value={type}
              onChange={changeType}
            />
          </FormRow>
          <FormRow>
            <Label>利益配分率</Label>
            <TextForm
              type="number"
              width="100px"
              value={profitShareRate}
              placeholder="例）50"
              required
              onChange={changeProfitShareRate}
              isError={profitShareRateError}
              errorMessage="0から100の間で入力してください"
            />
            <Label>%</Label>
          </FormRow>
        </ContentArea>
        <FooterArea>
          <CancelWrap>
            <Button type="secondary" onClick={onCancel} label="キャンセル" width="160px" />
          </CancelWrap>
          <SaveWrap>
            <Annotation>追加するまで変更内容は反映されません</Annotation>
            <Button
              type="primary"
              onClick={onClickSubmit}
              label={mode === "create" ? "貸主を追加" : "編集内容を保存"}
              disabled={!canSubmit}
              width="160px"
            />
          </SaveWrap>
        </FooterArea>
      </Board>
      <UserModal isVisible={isModalShow} config={modalConfig} />
    </>
  );
};

export default BaseOwnerEntityForm;
